/**
 * useAgentChat — consumer hook for agent chat episodes.
 *
 * Subscribes to runtime.agent.chat store for a given contextKey
 * and exposes action callbacks.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type { ChatMessageAttachment, EpisodeState, UIContext } from "../types";

export interface UseAgentChatResult {
  readonly messages: EpisodeState["messages"];
  readonly streamingContent: string;
  readonly isStreaming: boolean;
  readonly toolCalls: EpisodeState["toolCalls"];
  readonly toolResults: EpisodeState["toolResults"];
  readonly contentBlocks: EpisodeState["contentBlocks"];
  readonly error: EpisodeState["error"];
  readonly episodeId: string | null;
  readonly episodeTitle: string | null;
  readonly replyTo: EpisodeState["replyTo"];
  readonly sendMessage: (text: string, context?: UIContext, attachments?: readonly ChatMessageAttachment[], episodeTitle?: string, systemPrompt?: string, engine?: string, model?: string) => void;
  readonly stopStream: () => void;
  readonly approveToolCall: (toolCallId: string, argumentsOverride?: unknown) => Promise<void>;
  readonly denyToolCall: (toolCallId: string) => Promise<void>;
  readonly startNewEpisode: () => void;
}

const EMPTY: EpisodeState = {
  episodeId: null, episodeTitle: null, replyTo: null, messages: [], streamingContent: "",
  isStreaming: false, pausedForApproval: false, toolCalls: [], toolResults: [],
  contentBlocks: [], error: null,
  // No episode yet, so nothing has been chosen — and `engineLocked: false`
  // here is a statement about an episode that does not exist, not a default
  // standing in for one that does.
  engine: null, engineLocked: false, model: null, lastTurnResolution: null,
};

export function useAgentChat(contextKey: string, contextEntityId?: string, explicitEpisodeId?: string): UseAgentChatResult {
  const runtime = useAppRuntime();
  const chat = runtime.agent.chat;
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const [state, setState] = useState<EpisodeState>(() => (chat.getState(contextKey) as EpisodeState) ?? EMPTY);
  const loadedRef = useRef<string | null>(null);

  useEffect(() => {
    const existing = chat.getState(contextKey) as EpisodeState | undefined;
    setState(existing ?? EMPTY);

    const unsub = chat.subscribe(contextKey, (s) => { setState({ ...(s as EpisodeState) }); });

    const loadKey = explicitEpisodeId
      ? `${contextKey}:episode:${explicitEpisodeId}`
      : `${contextKey}:entity:${contextEntityId ?? ""}`;
    const loaded = explicitEpisodeId
      ? existing?.episodeId === explicitEpisodeId
      : existing != null;

    if (!loaded && loadedRef.current !== loadKey) {
      loadedRef.current = loadKey;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async (): Promise<void> => {
        let loadedEpisodeId: string | null;
        if (explicitEpisodeId) {
          await chat.loadEpisodeById(contextKey, explicitEpisodeId);
          loadedEpisodeId = explicitEpisodeId;
        } else {
          loadedEpisodeId = await chat.loadLatestEpisode(contextKey, contextEntityId);
        }
        if (!loadedEpisodeId) {
          loadedRef.current = null;
        }
      })();
    }

    return unsub;
  }, [contextKey, chat, contextEntityId, explicitEpisodeId]);

  const sendMessage = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (text: string, context?: UIContext, attachments?: readonly ChatMessageAttachment[], episodeTitle?: string, systemPrompt?: string, engine?: string, model?: string) => {
      void chat.sendMessage(
        contextKey,
        text,
        context,
        undefined,
        contextEntityId,
        attachments,
        undefined,
        episodeTitle,
        systemPrompt,
        engine,
        model,
      ).finally(() => {
        void queryClient.invalidateQueries();
      });
    },
    [chat, contextKey, contextEntityId, queryClient],
  );
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  const startNewEpisode = useCallback(() => { chat.startNewEpisode(contextKey); }, [chat, contextKey]);
  const handleStop = useCallback(() => { chat.stopStream(contextKey); }, [chat, contextKey]);
  const handleApprove = useCallback(async (id: string, argumentsOverride?: unknown) => {
    await chat.approveToolCall(contextKey, id, true, argumentsOverride);
    // After a tool is approved & executed, invalidate all module queries
    // so note detail, contacts, etc. pick up changes made by the agent.
    void queryClient.invalidateQueries();
  }, [chat, contextKey, queryClient]);
  const handleDeny = useCallback(async (id: string) => { await chat.approveToolCall(contextKey, id, false); }, [chat, contextKey]);

  return {
    messages: state.messages,
    streamingContent: state.streamingContent,
    isStreaming: state.isStreaming,
    toolCalls: state.toolCalls,
    toolResults: state.toolResults,
    contentBlocks: state.contentBlocks,
    error: state.error,
    episodeId: state.episodeId,
    episodeTitle: state.episodeTitle,
    replyTo: state.replyTo,
    sendMessage,
    stopStream: handleStop,
    approveToolCall: handleApprove,
    denyToolCall: handleDeny,
    startNewEpisode,
  };
}
